import { Module } from "vuex";
import store from ".";
import { $api } from "@/services/api";
import useChecklistHelpers from "@/use/checklistHelpers";
import type { Stipulation } from "@/models/opal";
import type { SortedCheckListItem } from "@/models/checklist";
import {
  StipulationStatusIds,
  ProcessingCategoryIds,
  PayoffVerificationStipulationTypeIds,
  EsignStipulationTypeIds,
  RegulatoryStipulationTypeIds,
  ASSIGNED_APPLICANT_ONLY,
} from "@/config/constants";
import { DocumentEntityTypes } from "@/models/enums";

const { title, processingCategoryTitle, groupItems, isComplete, getItemStatus, replaceSortedItem } =
  useChecklistHelpers();

interface UpdateStipVuexStatePayload {
  stipulation: Stipulation;
  title: string;
}

const checklistModule: Module<any, any> = {
  namespaced: true,
  state: {
    checklistItems: [],
    sortedApplicationStips: {},
    sortedItems: [],
    esignAvailable: false,
    esignCompleted: false,
    showIncomeVerification: undefined as undefined | boolean,
    showCompletedItems: false,
    incomeSources: [],
  },
  getters: {
    rateAndRepaymentStipsExist() {
      const keys = Object.keys(store.state.checklistModule.sortedApplicationStips);
      if (keys.includes("Rate and Repayment")) {
        return store.state.checklistModule.sortedApplicationStips["Rate and Repayment"]?.length > 0;
      } else {
        return false;
      }
    },
    getUploadMetadata: (state: any) => (id: number, oidcUser: any) => {
      for (const item of state.sortedItems) {
        const itemIndex = item.items.findIndex((i: any) => i.id === id);
        if (itemIndex !== -1) {
          const foundItem = item.items[itemIndex];
          const metadata = {
            entityId: foundItem.applicationId,
            personId: foundItem.applicantId,
            entityTypeId: DocumentEntityTypes.Application,
            userId: oidcUser,
            documentTypeId: foundItem.docTypeId || 23,
            applicantVisibility: true,
            stipulationId: foundItem.id,
          };
          return metadata;
        }
      }
      return null; // return something if not found
    },
    sortedItems(state: any) {
      return state.sortedItems;
    },
    esignAvailable: (state: any) => {
      return state.esignAvailable;
    },
    esignCompleted: (state: any) => {
      return state.esignCompleted;
    },
    incomeSources: (state: any) => {
      return state.incomeSources;
    },
    finalDocumentationStipExists: (state: any) => {
      const stips = store.state.checklistModule.sortedApplicationStips[
        "Final Documentation"
      ]?.filter(
        (s: any) =>
          s.stipulationTypeId === EsignStipulationTypeIds.ConfirmationEsign &&
          s.applicantId === store.state.appData.currentApplicant.id,
      );
      return stips?.length > 0
        ? stips[0]?.stipulationStatusId === StipulationStatusIds.OPEN
        : false;
    },
  },
  mutations: {
    setSortedStips(state: any, sortedStips: any[]) {
      state.sortedStips = sortedStips;
    },
    setSortedItems(state: any, sorted: any) {
      state.sortedItems = sorted;
    },
    setSortedApplicationStips(state: any, stips: any) {
      state.sortedApplicationStips = stips;
    },
    esignAvailable(state: any, flag: boolean) {
      state.esignAvailable = flag;
    },
    esignComplete(state: any, flag: boolean) {
      state.esignCompleted = flag;
    },
    addChecklistItem(state: any, item: any) {
      state.checklistItems.push(item);
    },
    setChecklistItems(state: any, items: any) {
      state.checklistItems = items;
    },
    setShowCompletedItems(state: any, flag: boolean) {
      state.showCompletedItems = flag;
    },
    updateSortedChecklistItem(state: any, stip: any) {
      state.checklistModule.sortedApplicationStips[stip.title] = stip;
    },
    setIncomeSources(state: any, sources: any) {
      state.checklistModule.incomeSources = sources;
    },
    setShowIncomeVerification(state: any, flag: boolean) {
      state.showIncomeVerification = flag;
    },
    updateStipVuexState(state: any, payload: UpdateStipVuexStatePayload) {
      const sortedApplicationStipsIndex = state.sortedApplicationStips[payload.title].findIndex(
        (s: Stipulation) => s.id === payload.stipulation.id,
      );
      if (sortedApplicationStipsIndex !== -1) {
        state.sortedApplicationStips[payload.title].splice(
          sortedApplicationStipsIndex,
          1,
          payload.stipulation,
        );
      }
      const sortedItemsStipsIndex = state.sortedItems.findIndex(
        (i: SortedCheckListItem) => i.title === payload.title,
      );

      if (sortedItemsStipsIndex !== -1) {
        const stipIndex = state.sortedItems[sortedItemsStipsIndex].items.findIndex(
          (s: Stipulation) => s.id === payload.stipulation.id,
        );
        if (stipIndex !== -1) {
          state.sortedItems[sortedItemsStipsIndex].items.splice(
            sortedItemsStipsIndex,
            1,
            payload.stipulation,
          );

          state.sortedItems[sortedItemsStipsIndex].complete = isComplete(
            state.sortedItems[sortedItemsStipsIndex].items,
          );
        }
      }

      store.dispatch("updateStipsDataStip", payload.stipulation);
    },
    getSortedChecklistItemStatuses(state: any) {
      state.sortedItems.forEach((item: SortedCheckListItem) => {
        item.status = getItemStatus(item.items);
        item.complete = isComplete(item.items);
      });
      state.completedItems = state.sortedItems.filter((item: SortedCheckListItem) => item.complete);
      state.showCompletedItems = state.completedItems?.length > 0;
    },
    getSortedChecklistItemStatusByName(state: any, name: string) {
      state.sortedItems.forEach((item: SortedCheckListItem) => {
        if (name === item.name) {
          item.status = getItemStatus(item.items);
          item.complete = isComplete(item.items);
        }
      });

      state.completedItems = state.sortedItems.filter((item: SortedCheckListItem) => item.complete);
      state.showCompletedItems = state.completedItems?.length > 0;
    },
    getSortedChecklistItemStatusByTitle(state: any, title: string) {
      state.sortedItems.forEach((item: SortedCheckListItem) => {
        if (title === item.title) {
          item.status = getItemStatus(item.items);
          item.complete = isComplete(item.items);
        }
      });

      state.completedItems = state.sortedItems.filter((item: SortedCheckListItem) => item.complete);
      state.showCompletedItems = state.completedItems?.length > 0;
    },

    setSortedChecklistItemSubitemStatus(
      state: any,
      payload: {
        name: string;
        stipulationId: number;
        stipulationStatusId: number;
        stipulationStatusName: string;
      },
    ) {
      state.sortedItems.forEach((item: SortedCheckListItem) => {
        if (payload.name === item.name) {
          const stip = item.items.find((stip: Stipulation) => stip.id === payload.stipulationId);
          if (stip) {
            stip.stipulationStatusId = payload.stipulationId;
            stip.stipulationStatusName = payload.stipulationStatusName;
          }
        }
      });
    },
  },
  actions: {
    sortStips({ commit }, payload) {
      commit("setSortedStips", payload);
    },
    setSortedItems({ commit }, payload) {
      commit("setSortedItems", payload);
    },
    setSortedApplicationStips({ commit }, payload) {
      commit("setSortedApplicationStips", payload);
    },
    setEsignAvailableFlag({ commit }, payload) {
      commit("esignAvailable", payload);
    },
    setEsignCompleteFlag({ commit }, payload) {
      commit("esignComplete", payload);
    },
    setShowCompletedItems({ commit }, payload) {
      commit("setShowCompletedItems", payload);
    },
    updateSortedChecklistItem({ commit }, payload) {
      commit("updateSortedChecklistItem", payload);
    },
    setIncomeSources({ commit }, payload) {
      commit("setIncomeSources", payload);
    },
    setShowIncomeVerification({ commit }, payload) {
      commit("setShowIncomeVerification", payload);
    },
    getSortedItemCompleteStatusByTitle({ commit, state }, payload) {
      const sortedItem = state.sortedItems.find((item: any) => item.title === payload);

      sortedItem.complete = isComplete(sortedItem.items);
      commit("setSortedItems", state.sortedItems);

      const completedItems = state.sortedItems.filter((item: any) => item.complete);
      commit("setShowCompletedItems", completedItems.length > 0);
    },
    async updateApplicationStipulationStatus({ commit }, payload) {
      const stipulation = store.state.stipsData.find((s: Stipulation) => s.id === payload);
      const stipulationStatusId = store.state.lookupsModule.lookups.stipStatuses.find(
        (s: any) => s.name === `${payload.status}`,
      );
      const userId = store.state.oidcStore.oidcUser.sub;
      const updateStipRequest = {
        stipulationStatusId: stipulationStatusId,
        updatedByUserId: userId,
      };

      const response = await $api.applications.updateApplicationStipulation(
        stipulation.applicationId,
        stipulation.id,
        updateStipRequest,
      );
      console.log("updateApplicationStipulationStatus()", response);
    },
    updateStipVuexState({ commit }, payload: UpdateStipVuexStatePayload) {
      commit("updateStipVuexState", payload);
    },
    async getSortedChecklistItems({ commit }, payload?: { refresh: boolean }) {
      const applicationStips = await $api.applications.getApplicationStips(
        store.state.appData.application?.id,
      );
      if ((applicationStips?.results || []).length > 0) {
        store.dispatch("setStipsData", applicationStips?.results);
        const activeStips = applicationStips?.results.filter(
          (s: any) => s.stipulationStatusId !== StipulationStatusIds.CANCELLED,
        );

        const currentApplicantId = store?.state?.appData?.currentApplicant?.id;

        // Income stips that only show for applicant
        const filterIncomeStips = (stip: Stipulation) => {
          // If stip not applicant visible only -- show to both
          if (
            stip.processingCategoryId === ProcessingCategoryIds.Income &&
            !ASSIGNED_APPLICANT_ONLY.includes(stip.stipulationTypeId)
          ) {
            return true;
          } else if (
            stip.processingCategoryId === ProcessingCategoryIds.Income &&
            ASSIGNED_APPLICANT_ONLY.includes(stip.stipulationTypeId) &&
            stip.applicantId === currentApplicantId
          ) {
            return true;
          } else {
            return false;
          }
        };

        const filteredStips = activeStips?.filter(
          (s: Stipulation) =>
            s.processingCategoryName === "RateTerm" ||
            s.processingCategoryName === "EligibilityVerification" ||
            s.processingCategoryName === "Citizenship" ||
            s.processingCategoryName === "SpousalConsent" ||
            filterIncomeStips(s) ||
            (s.processingCategoryId === ProcessingCategoryIds.ESign &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeId === EsignStipulationTypeIds.ExecuteLegalDocPackage &&
              s.applicantId === currentApplicantId) ||
            (s.processingCategoryId === ProcessingCategoryIds.Regulatory &&
              s.stipulationTypeId === RegulatoryStipulationTypeIds.SelfCertificationDetails &&
              s.applicantId === currentApplicantId) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId === PayoffVerificationStipulationTypeIds.ConfirmPayoffRequest) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId === PayoffVerificationStipulationTypeIds.LoanAccountStatement) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId ===
                PayoffVerificationStipulationTypeIds.LoanResponsibilityAffidavit &&
              s.applicantId === currentApplicantId) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId === PayoffVerificationStipulationTypeIds.PayoffStatement) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId === PayoffVerificationStipulationTypeIds.ServicerPayoffAddress) ||
            (s.processingCategoryId === ProcessingCategoryIds.PayoffVerification &&
              s.stipulationTypeId ===
                PayoffVerificationStipulationTypeIds.ResponsibilityForUnderPayment &&
              s.applicantId === currentApplicantId) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "StateIssuedID".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "Passport".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "BirthCertificate".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "SchoolID".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "SocialSecurityCard".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "TaxFormNameSSN".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "OtherIDVerification".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "ITINLetterOrCard".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "ProofOfAddress".toLowerCase()) ||
            (s.processingCategoryName === "Identity" &&
              s.stipulationTypeName.toLowerCase() === "NameVerification".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeName.toLowerCase() === "AddressVerification".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeName.toLowerCase() === "DateOfBirthVerification".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeName.toLowerCase() === "SSNVerification".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            s.stipulationTypeName.toLowerCase() === "ADDMismatch".toLowerCase() ||
            s.stipulationTypeName.toLowerCase() === "SSNMistmatchProofSSN".toLowerCase() ||
            (s.stipulationTypeName.toLowerCase() === "UnfreezeCredit".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeName.toLowerCase() === "UnSuppressBureau".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            (s.stipulationTypeName.toLowerCase() === "ContactCBRCorrection".toLowerCase() &&
              s.applicantId === currentApplicantId) ||
            s.stipulationTypeName.toLowerCase() === "EstablishMembership".toLowerCase(),
        );

        filteredStips.forEach((result: any) => {
          result.processingCategoryTitle = processingCategoryTitle(result);
        });

        const sortedApplicationStips = groupItems(filteredStips, "processingCategoryTitle");
        const stipGroups = Object.keys(sortedApplicationStips);

        const sortedItems = [] as any[];

        const items = stipGroups.map((k: string) => {
          return {
            name: k.replace(/\s+/g, ""),
            title: k,
            formattedTitle: title(k),
            complete: isComplete(Object.values(sortedApplicationStips[k])),
            status: getItemStatus(Object.values(sortedApplicationStips[k])),
            items: Object.values(sortedApplicationStips[k]),
          };
        });

        items?.forEach((item: any) => {
          // check if there is no item present
          const newItem = sortedItems?.find((s: any) => s.title === item.title) === undefined;

          if (newItem === false) {
            replaceSortedItem(sortedItems, item.title, item);
          } else {
            sortedItems?.push(item);
          }
        });

        const completedItems = sortedItems.filter((item: any) => item.complete);
        commit("setSortedItems", sortedItems);
        commit("setShowCompletedItems", completedItems.length > 0);
        commit("setSortedApplicationStips", sortedApplicationStips);
        store.dispatch("checklistModule/getEsignStatus");
      }

      if (payload?.refresh && (applicationStips?.results || []).length == 0) {
        // reset the values
        store.dispatch("setStipsData", applicationStips?.results);
        commit("setSortedItems", []);
        commit("setShowCompletedItems", false);
        commit("setSortedApplicationStips", {});
      }
    },
    resetChecklist({ commit }) {
      store.dispatch("setStipsData", []);
      commit("setSortedItems", []);
      commit("setShowCompletedItems", false);
      commit("setSortedApplicationStips", {});
    },
    getSortedCheklistItemStatuses({ commit }) {
      commit("getSortedChecklistItemStatuses");
    },
    getSortedChecklistItemStatusByName({ commit }, name: string) {
      commit("getSortedChecklistItemStatusByName", name);
    },
    getSortedChecklistItemStatusByTitle({ commit }, title: string) {
      commit("getSortedChecklistItemStatusByTitle", title);
    },
    async getRateAndRepaymentStips({ state, commit }) {
      const applicationStips = await $api.applications.getApplicationStips(
        store.state.appData.application?.id,
      );
      console.log("getRateAndRepaymentStips", applicationStips);

      if ((applicationStips?.results || []).length > 0) {
        const activeStips = applicationStips?.results.filter(
          (s: any) => s.stipulationStatusId !== StipulationStatusIds.CANCELLED,
        );
        const filteredStips = activeStips?.filter(
          (s: any) =>
            s.processingCategoryName === "RateTerm" ||
            s.processingCategoryName === "LoanConfiguration",
        );

        filteredStips.forEach((result: any) => {
          result.processingCategoryTitle = processingCategoryTitle(result);
        });

        const sortedApplicationStips = groupItems(filteredStips, "processingCategoryTitle");
        const stipGroups = Object.keys(sortedApplicationStips);
        const sortedItems = state.sortedItems;
        const items = stipGroups.map((k: string) => {
          return {
            name: k.replace(/\s+/g, ""),
            title: k,
            formattedTitle: title(k),
            complete: isComplete(Object.values(sortedApplicationStips[k])),
            status: getItemStatus(Object.values(sortedApplicationStips[k])),
            items: Object.values(sortedApplicationStips[k]),
          };
        });

        items?.forEach((item: any) => {
          // check if there is no item present
          const newItem = sortedItems?.find((s: any) => s.title === item.title) === undefined;

          if (newItem === false) {
            replaceSortedItem(sortedItems, item.title, item);
          } else {
            sortedItems?.push(item);
          }
        });

        const sortedApplicationStipsState = state.sortedApplicationStips;
        sortedApplicationStipsState["Rate and Repayment"] = Object.values(
          sortedApplicationStips["Rate and Repayment"],
        );
        commit("setSortedItems", sortedItems);
        commit("setSortedApplicationStips", sortedApplicationStipsState);
      }
    },
    getEsignStatus({ commit }) {
      const esignAvailableFlag =
        store.state.checklistModule.sortedApplicationStips["Final Documentation"] &&
        store.state.checklistModule.sortedApplicationStips["Final Documentation"].length > 0;
      commit("esignAvailable", esignAvailableFlag);
      if (esignAvailableFlag) {
        const esignStips = store.state.checklistModule.sortedApplicationStips[
          "Final Documentation"
        ].filter(
          (s: any) =>
            (s.stipulationTypeId === EsignStipulationTypeIds.ConfirmationEsign ||
              s.stipulationTypeId === EsignStipulationTypeIds.ExecuteLegalDocPackage) &&
            s.applicantId === store.state.appData.currentApplicant.id,
        );

        const completed = esignStips[0]?.stipulationStatusName === "Completed";
        commit("esignComplete", completed);
      }
    },
  },
};

export default checklistModule;
